<template>
  <div />
</template>
<script>
import { parseISO, getUnixTime, subDays } from 'date-fns'

import {
  getInitialData,
  subscribeToEventUpdates,
  // subscribeToResponses,
  fetchNewParticipants,
  fetchNewResponses
} from '~/queries/eventQueries.gql'

import getEventIdFromShortcode from '~/queries/getEventIdFromShortcode'
import { setLastAction } from '~/queries/saveEventState'

export default {
  // props: ['currentSlide'],
  data () {
    return {
      eventId: '',
      mainEventId: '',
      primaryUser: '',
      currentSlide: null,
      lastReceivedPlayerId: -1,
      removedPlayerCount: 0,
      eventStartTime: null,
      earliestActiveResponse: 930000 // this helps narrow the query to only responses after this id
    }
  },
  computed: {
    shortcode () {
      return this.hasValidCodeInUrl
        ? this.$route.params.id.trim().toLowerCase()
        : this.$parent.code.trim().toLowerCase()
    },
    hasValidCodeInUrl () {
      return this.$route.params.id && /[a-zA-Z0-9]{3,10}/.test(this.$route.params.id)
    },
    isOwner () {
      return this.$auth.user && (this.$auth.user.id === this.primaryUser)
    },
    isSpectatorEvent () {
      return this.$route.name === 'spectate'
    },
    isMainEvent () {
      return !this.isSpectatorEvent
    },
    eventMeta () {
      return this.$parent.eventMeta
    },
    startTime () {
      return this.eventStartTime || getUnixTime(subDays(new Date(), 7))
    },
    participants () {
      return this.$parent.realParticipants // this excludes demo participants from being merged
    },
    responses () {
      return this.$parent.realResponses // exclude demo responses
    },
    activeEvent () {
      return this.$store.state.activeEvent
    },
    currentSlideTemplate () {
      return this.$parent.currentSlide ? this.$parent.currentSlide.template : false
    },
    responsesBySlideView () {
      return this.responsesBySlide
    },
    playerResponseGroup () {
      const currentPlayerId = this.$store.state.activeEvent.player.id

      if (!currentPlayerId) { return 1 }
      const playersInJoinOrder = this.participants.slice().reverse()

      const playerIndex = playersInJoinOrder.findIndex((player) => { return player.id === currentPlayerId })

      return Math.ceil((playerIndex + 1) / 100 % 100)
    }
  },
  created () {
    this.responsesBySlide = {}
  },
  async mounted () {
    this.log('responsesBySlide: ', this.responsesBySlide)
    await this.getEventId(this.shortcode) // shortcode, primary user, etc

    if (!this.eventId || this.eventId.length < 10) {
      this.$buefy.toast.open({ type: 'is-danger', message: 'A network error has occured, please try again' })
      console.error({ msg: 'Error mounting EventSubscription for event ', shortcode: this.shortcode })
      this.$router.push('/go')
      return
    }

    if (this.isOwner) {
      this.setLastAction()
      this.isHostJoinedAsPlayer()
    }

    // refresh server and client time offset to sync timers
    this.$store.dispatch('setServerTime')

    await this.getInitialData()
    await this.subscribeToUpdates()
    // await this.subscribeToResponses()
  },
  methods: {
    isHostJoinedAsPlayer () {
      this.log('checking if host was joined as player. event id: ' + this.eventId)

      // exit if host already joined as player
      if (this.$store.state.hostJoinedAsPlayer) { return }

      // exit if host was never a player (no query string)
      if (this.$route.query.player !== 'true') { return }

      // if previous activeEvent has a different code but player query is present, redirect to /go
      if (this.activeEvent.shortcode !== this.shortcode) { this.$router.push('/go'); return }

      // if activeEvent has same shortcode but different id, redirect to /go
      if (this.activeEvent.id !== this.eventId) { this.$router.push('/' + this.shortcode); return }

      // if host has an active player and player in query string in this event, set hostJoinedAsPlayer to true
      if (!isNaN(this.activeEvent.player.id)) {
        this.log('host previously joined as player: ', this.activeEvent.player.name)
        this.$store.commit('setHostAsPlayer', true)
      }
    },
    getCurrentSlideResponses () {
      if (!this.responsesBySlide[this.currentSlide]) { return [] }
      return this.responsesBySlide[this.currentSlide].responses
    },
    getCurrentSlideLastResponseId () {
      if (!this.responsesBySlide[this.currentSlide]) { return this.earliestActiveResponse }
      return this.responsesBySlide[this.currentSlide].lastResponseId || this.earliestActiveResponse
    },
    async getEventId () {
      let data
      try {
        const resp = await this.$apollo.query({
          query: getEventIdFromShortcode,
          variables: { shortcode: this.shortcode.toLowerCase() },
          fetchPolicy: 'no-cache'
        })
        data = resp.data
      } catch (e) {
        this.$buefy.toast.open({ type: 'is-danger', message: 'A network error has occured, please try again' })
        this.$sentry.captureException(new Error(JSON.stringify({
          msg: 'Error at EventSubscription: getEventIdFromShortcode',
          error: e,
          shortcode: this.shortcode
        })))
        this.$router.push('/go')
        return
      }

      // redirect if no active events are found
      if (!data.events || data.events.length < 1 || !data.events[0] || !data.events[0].id) {
        this.$sentry.captureException(new Error(JSON.stringify({
          msg: 'Attempted to subscribe to event but it was not found: ',
          data,
          shortcode: this.shortcode
        })))
        this.$buefy.toast.open({ type: 'is-danger', message: 'Event has ended or does not exist, please try again' })
        this.$router.push('/go')
        return
      }

      if (this.isSpectatorEvent) {
        this.eventId = data.events[0]?.settings?.spectatorEventId
        this.mainEventId = data.events[0].id
        this.log('starting event subscription for spectator event: ', this.eventId, data)
      } else {
        this.eventId = data.events[0].id
        this.mainEventId = data.events[0].id
      }

      this.primaryUser = data.events[0].primaryUser
      this.eventStartTime = data.events[0].startTime

      this.$emit('update:eventId', { eventId: this.eventId, primaryUser: this.primaryUser })

      // if user is not a player and is not the owner, redirect
      if (!this.isOwner && !this.isTest && !this.activeEvent.player.id) {
        this.$buefy.toast.open({ type: 'is-danger', message: 'You have to join this event first!' })
        this.$router.push('/' + this.shortcode)
      }

      // if activePlayer is in a different event, redirect
      if (!this.isOwner && !this.isTest && this.shortcode === this.activeEvent.shortcode && this.activeEvent.id !== this.eventId) {
        this.$buefy.toast.open({ type: 'is-danger', message: 'You have to join this event first!' })
        this.$router.push('/' + this.shortcode)
      }

      /*
      * TODO: if spectator event, make sure user has joined spectator event
      * above conditional may also handle that
      */
    },
    async setLastAction () {
      await this.$apollo.mutate({
        mutation: setLastAction,
        variables: {
          eventId: this.eventId,
          now: new Date().toISOString()
        }
      })
    },
    async getInitialData () {
      this.log('fetching initial event data')
      const { data } = await this.$apollo.query({
        query: getInitialData,
        variables: { id: this.eventId, startTime: this.startTime },
        fetchPolicy: 'no-cache'
      })

      const event = data.events_by_pk

      this.$emit('update:slideDeck', event.slideDeckCopy)
      this.$store.commit('setSlideDeck', { ...event.slideDeckCopy })

      const eventMeta = {
        id: event.id,
        votes: [],
        startTime: event.startTime,
        lastUpdate: event.lastUpdate,
        position: event.position,
        shortcode: event.shortcode || this.$route?.params?.id,
        primaryUser: event.primaryUser,
        slideState: event.slideState,
        settings: event.settings,
        hostPlan: event.settings ? event.settings.hostPlan : null,
        deckId: event.slideDeck,
        timerEnd: event.timerEnd,
        isActive: event.isActive,
        reactions: []
      }

      if (Number.isInteger(eventMeta.settings.lowestResponseId)) {
        this.earliestActiveResponse = eventMeta.settings.lowestResponseId
      }

      this.currentSlide = event.currentSlide

      const latestPlayer = event.participants[0]
      this.log('initial data fetched, latest player: ', latestPlayer)
      this.lastReceivedPlayerId = latestPlayer?.id || -1

      // sort as for passTheMic
      const sortedPlayers = [...event.participants].sort((a, b) => { return a.id - b.id })
      this.$emit('update:eventMeta', eventMeta)
      this.$emit('update:players', sortedPlayers)
      this.$emit('update:responses', [])
    },
    async subscribeToUpdates () {
      await this.$apollo.addSmartSubscription('event', {
        query: subscribeToEventUpdates,
        variables () {
          return {
            id: this.eventId,
            startTime: this.startTime
          }
        },
        async result ({ data }) {
          const event = data.events_by_pk
          this.log(`new event update data, position: ${event.currentSlide}. Last response: ${event.lastResponseThisSlide}`)

          if (this.isTest) {
            // console.log('Timing: event data returned ' + (Date.now() - this.lastUpdate) + 'ms')
            this.lastUpdate = Date.now()
          }

          const eventMeta = { ...this.eventMeta }

          eventMeta.votes = event.votes
          eventMeta.lastUpdate = event.lastUpdate
          eventMeta.position = event.position
          eventMeta.slideState = event.slideState
          eventMeta.settings = event.settings
          eventMeta.reactions = this.getFreshReactions(event.reactions, event.lastUpdate)
          eventMeta.timerEnd = event.timerEnd
          eventMeta.isActive = event.isActive

          this.$emit('update:eventMeta', eventMeta)

          if (event.settings.removedPlayers && event.settings.removedPlayers.length > this.removedPlayerCount) {
            this.removedPlayerCount = event.settings.removedPlayers.length
            this.clearRemovedPlayerData()
          }

          if (event.participants.length > 0 && event.participants[0].id > this.lastReceivedPlayerId) {
            await this.getNewPlayers()
          }

          this.currentSlide = event.currentSlide

          // fetch new responses
          const lastResponseThisSlide = event.lastResponseThisSlide

          if (!lastResponseThisSlide || !this.responsesBySlide[this.currentSlide] || !this.responsesBySlide[this.currentSlide].lastResponseId || (lastResponseThisSlide && lastResponseThisSlide > this.responsesBySlide[this.currentSlide].lastResponseId)) {
            await this.getNewResponses(lastResponseThisSlide)
          } else {
            // set slide responses to whatever we have locally
            this.$emit('update:responses', this.responsesBySlide[this.currentSlide].responses)
          }
        },
        fetchPolicy: 'no-cache'
      })
    },
    clearRemovedPlayerData () {
      this.log('clearing removed player responses from responsesBySlide cache in EventSubscription.vue')
      const removedPlayers = this.$parent.removedPlayers

      // clear local data of all removed players and removed responses
      for (const slideNum in this.responsesBySlide) {
        const responses = this.responsesBySlide[slideNum].responses
        this.responsesBySlide[slideNum].responses = responses.filter((r) => {
          return !removedPlayers[r.participantId]
        })
      }
    },
    async getNewPlayers () {
      this.log('new player found, fetching all new players, latest Id: ', this.lastReceivedPlayerId)
      const { data } = await this.$apollo.query({
        query: fetchNewParticipants,
        variables: {
          eventId: this.eventId,
          lastReceivedId: this.lastReceivedPlayerId,
          startTime: this.startTime
        },
        fetchPolicy: 'no-cache'
      })

      const latestPlayer = data.participants[0]
      this.log('new players fetched, latest: ', latestPlayer)
      this.lastReceivedPlayerId = latestPlayer?.id || -1

      const mergedPlayers = this.participants ? [...this.participants, ...data.participants] : data.participants

      // sort as for passTheMic
      mergedPlayers.sort((a, b) => { return a.id - b.id })

      this.$emit('update:players', mergedPlayers)
    },
    async getNewResponses (newestResponse) {
      // don't try to fetch responses if this is a static slide
      const staticSlides = ['joinSlide', 'titleSlide', 'contentSlide', 'scoreSummary', 'passTheMic']
      if (!this.currentSlideTemplate || staticSlides.includes(this.currentSlideTemplate)) {
        this.log('no currentSlide or static slide: not looking for responses')
        return
      }

      const currentSlideLastResponseId = this.getCurrentSlideLastResponseId()

      const start = Date.now()
      this.log('starting to fetch responses from ' + currentSlideLastResponseId + ' to ' + newestResponse)

      const variables = {
        eventId: this.eventId,
        currentSlide: this.currentSlide,
        startTime: this.startTime,
        lastReceivedId: currentSlideLastResponseId
      }

      const { data } = await this.$apollo.query({
        query: fetchNewResponses,
        variables,
        fetchPolicy: 'no-cache'
      })

      /// CloudFlare DurableObject caching method
      // const responseApi = this.$store.state.siteUrl + '/get-responses/'
      // let params = `?event=${this.eventId}&slide=${this.currentSlide}&from=${currentSlideLastResponseId}&group=${this.playerResponseGroup}`
      // if (newestResponse) {
      //   params = params + `&to=${newestResponse}`
      // }
      // const data = await this.$axios.$get(responseApi + params)
      // this.log('response data: ', data)

      const end = Date.now()

      this.log('fetching new responses took ' + (end - start) + 'ms')

      const currentSlideResponses = this.getCurrentSlideResponses()

      if (data.responses.length < 1) {
        this.log('no responses found from query at slide ' + this.currentSlide + ', setting responses to currentSlideResponses ')
        this.$emit('update:responses', currentSlideResponses)
        return
      }

      this.log('new responses found: ', data)
      this.log('old responses: ', currentSlideResponses)

      const mergedResponses = [...currentSlideResponses, ...data.responses]

      this.responsesBySlide[this.currentSlide] = {
        responses: mergedResponses,
        lastResponseId: data.responses[0].id
      }

      this.log('updating responses to: ', mergedResponses)
      this.$emit('update:responses', mergedResponses)
    },
    getFreshReactions (reactions, lastUpdate) {
      return reactions.filter((react) => {
        const updated = getUnixTime(parseISO(lastUpdate))
        const reactTime = getUnixTime(parseISO(react.createdAt))
        return updated - reactTime < 5
      })
    }
  }
}
</script>
